import { ChangeEvent, ChangeEventHandler, useContext, useState } from 'react';

import { Box, BoxProps } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { Address } from '../../../gql/addressGql';
import { Customer } from '../../../gql/customerGql';
import { Deal } from '../../../gql/dealGql';

import CardSubHeaderV2 from '../../shared/Card/components/CardSubHeaderV2';
import { GridFormColumn, GridFormRow } from '../../shared/GridForm';
import Input from '../../shared/Input';
import Select from '../../shared/Select';

import {
  COUNTRY_OPTIONS,
  addressConfigForCountry,
  countryFromString,
  countryRulesFor,
  isCanada,
} from '../../../constants/countries';
import { LDFlags } from '../../../constants/experiments';
import { regionOptionsForCountry } from '../../../constants/selectOptions';
import useFlag from '../../../hooks/useFlag';
import { DealContext } from '../../../libs/DealContext';
import { AutoExplodeZip } from '../../AutoExplodeZip';

type AddressFormProps = BoxProps & {
  name: keyof Pick<Deal, 'customer' | 'cobuyer'>;
  objectName: keyof Pick<Customer, 'address' | 'prev_address' | 'mailing_address'>;
  isUneditable: boolean;
  // Country is chosen when the deal is created and read-only afterwards.
  isCountryEditable?: boolean;
  customHandleChange?: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
};

export const AddressForm = ({
  name,
  objectName,
  isUneditable,
  isCountryEditable = false,
  customHandleChange,
  ...rest
}: AddressFormProps) => {
  const { deal } = useContext(DealContext);
  const {
    values,
    handleBlur,
    handleChange: formikHandleChange,
    setFieldValue,
  } = useFormikContext<Deal>();
  const handleChange = customHandleChange ?? formikHandleChange;
  const canadaEnabled = !!useFlag(LDFlags.DD_CANADA_DEALS);

  const [isAddressLineFocused, setAddressLineFocused] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  const address = values?.[name]?.[objectName] as Address | undefined;
  const country = countryFromString(address?.country ?? values.country ?? deal.country);
  const { regionLabel, postalCodeLabel, postalCodeMask } = addressConfigForCountry(country);
  const { hasCounty } = countryRulesFor(country);
  const showCountry = canadaEnabled || isCanada(country);
  const fieldName = `${name}.${objectName}`;

  const handleCountryChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const nextCountry = countryFromString(e.target.value);
    setFieldValue(`${fieldName}.country`, nextCountry);
    setFieldValue('country', nextCountry);
    setFieldValue(`${fieldName}.state`, '');
    setFieldValue(`${fieldName}.zip`, '');
    setFieldValue(`${fieldName}.county`, '');
  };

  const showAddressTitle = objectName === 'prev_address' || objectName === 'mailing_address';
  const addressTitle = objectName === 'prev_address' ? 'Previous Address' : 'Mailing Address';

  return (
    <Box {...rest}>
      {showAddressTitle && <CardSubHeaderV2 title={addressTitle} />}
      <GridFormColumn>
        <GridFormRow minChildWidth={180}>
          <Input
            label="Street Address"
            name={`${fieldName}.address_line`}
            onFocus={() => setAddressLineFocused(true)}
            onBlur={(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
              setAddressLineFocused(false);
              handleBlur(e);
            }}
            onChange={handleChange}
            isDisabled={isUneditable}
          />
          <Input
            label="Apt., Suite, Unit #"
            name={`${fieldName}.address_line_2`}
            onChange={handleChange}
            isDisabled={isUneditable}
          />
        </GridFormRow>
        <GridFormRow>
          {showCountry ? (
            <Select
              label="Country"
              emptyOption={false}
              name={`${fieldName}.country`}
              options={COUNTRY_OPTIONS}
              value={country}
              onChange={handleCountryChange}
              isDisabled={isUneditable || !isCountryEditable}
            />
          ) : null}
          <Input
            label={postalCodeLabel}
            name={`${fieldName}.zip`}
            onChange={(e) => {
              e.target.value = e.target.value.toUpperCase();
              handleChange(e);
            }}
            isDisabled={isUneditable}
            mask={postalCodeMask}
          />
          <Input
            label="City"
            name={`${fieldName}.city`}
            onChange={handleChange}
            isDisabled={isUneditable}
            isLoading={zipLoading}
          />
          <Select
            label={regionLabel}
            emptyOption={false}
            placeholder={regionLabel}
            name={`${fieldName}.state`}
            options={regionOptionsForCountry(country)}
            onChange={handleChange}
            isDisabled={isUneditable}
            isLoading={zipLoading}
          />
          {hasCounty ? (
            <Input
              label="County"
              name={`${fieldName}.county`}
              onChange={handleChange}
              isDisabled={isUneditable}
              isLoading={zipLoading}
            />
          ) : null}
        </GridFormRow>
      </GridFormColumn>
      <AutoExplodeZip
        name={name}
        objectName={objectName}
        isAddressLineFocused={isAddressLineFocused}
        setZipLoading={setZipLoading}
      />
    </Box>
  );
};
